import { getHighlightedRecordingId } from '@va/dashboard/selectors/app';
import { useVisitorRecordings } from '@va/dashboard/shared/recordings';
import { useFiltersContext } from '@va/shared/feature-filters';
import { storageItems } from '@va/standalone/shared/constants';
import { PageType, RecordingsDataType, RecordingsFiltersType, RecordingType } from '@va/types/recordings';
import { ControlledTableState, useControlledTableState } from '@va/ui/components/data-table';
import { useRefreshCallbacks } from '@va/util/components';
import { formatSessionDuration, LocalStorage } from '@va/util/helpers';
import moment from 'moment';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { KeyedMutator } from 'swr';
import { recordingsSelectOptionsEnum } from '../types';
import useDeleteRecordings, { DeleteRecordingsCallType } from '../useDeleteRecordings';

type RecordingsContextType = {
  currentRecording: RecordingType | null | undefined;
  updateCurrentRecording: (recording: RecordingType) => void;
  recordings: RecordingType[];
  totalPages: number;
  isLoading: boolean;
  pageSize: number;
  isAddNoteModalOpen: boolean;
  mutateRecordings: KeyedMutator<RecordingsDataType>;
  closeAddNoteModal: () => void;
  openAddNoteModal: (recordingId: string) => void;
  totalCount: number;
  pageNumber: number;
  selectedRecordingsIds: string[];
  selectRecording: (recordingId: string) => void;
  deselectRecording: (recordingId: string) => void;
  clearSelectedRecordingsIds: () => void;
  selectAllRecordings: () => void;
  isRecordingSelected: (recordingId: string) => boolean;
  selectedBulkOption: number | null;
  setSelectedBulkOption: (option: number | null) => void;
  deleteBulk: () => Promise<any>;
  deleteIndividual: (sessionIds: string[]) => Promise<any>;
  tableState: ControlledTableState;
  error: unknown;
};

export const RecordingsContext = createContext<RecordingsContextType>({} as RecordingsContextType);

type RecordingsContextProviderProps = PropsWithChildren<{
  config?: {
    initialPageSize?: number;
    initialFilters?: Partial<RecordingsFiltersType>;
  };
}>;

export const RecordingsContextProvider = ({ children, config }: RecordingsContextProviderProps) => {
  const highlightedRecordingId = useSelector(getHighlightedRecordingId);
  const { appliedFilterValues, reportBlockId } = useFiltersContext();
  const controlledTableState = useControlledTableState<string>(
    {
      rowSelection: {
        [highlightedRecordingId]: true,
      },
      pagination: {
        pageSize: config?.initialPageSize ?? 12,
        pageIndex: 0,
      },
      defaultSorting: [{ id: 'createdAt', desc: true }],
    },
    reportBlockId || 'recordings-table',
  );
  const { pagination } = controlledTableState;
  const { pageIndex, pageSize } = pagination;

  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [currentRecording, setCurrentRecording] = useState<RecordingType | undefined | null>(null);
  const [selectedRecordingsIds, setSelectedRecordingsIds] = useState<string[]>([]);
  const [selectedBulkOption, setSelectedBulkOption] = useState<recordingsSelectOptionsEnum | null>(null);

  const {
    data: recordingsData,
    isLoading,
    mutate: mutateRecordings,
    error,
  } = useVisitorRecordings({
    pageNumber: pageIndex,
    pageSize,
    initialFilters: config?.initialFilters,
    order: controlledTableState.sorting,
  });

  useRefreshCallbacks(mutateRecordings);

  const clearSelectedRecordingsIds = useCallback(() => {
    setSelectedRecordingsIds([]);
  }, []);

  const deleteBulk = useDeleteRecordings(DeleteRecordingsCallType.bulk);
  const deleteIndividual = useDeleteRecordings(DeleteRecordingsCallType.individual, clearSelectedRecordingsIds);

  const recordings = useMemo(() => recordingsData?.data ?? [], [recordingsData]);

  const totalCount = recordingsData?.sessionsTotal ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const mutateRecordingsTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const t = mutateRecordingsTimeout.current;
    return () => {
      if (t === null) return;
      clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    if (deleteBulk.isSucceeded || deleteIndividual.isSucceeded) {
      if (mutateRecordingsTimeout.current) {
        clearTimeout(mutateRecordingsTimeout.current);
      }

      mutateRecordingsTimeout.current = setTimeout(() => {
        mutateRecordings();
      }, 2000); // Deletion is not instantanious, until a better implementation is created BE side we need this timeout
    }
  }, [deleteBulk.isSucceeded, deleteIndividual.isSucceeded, mutateRecordings]);

  useEffect(() => {
    if (selectedRecordingsIds.length === 0) return;
    setSelectedRecordingsIds([]);
    setSelectedBulkOption(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilterValues]);

  useEffect(() => {
    const recordingIds = recordings.map((recording) => recording.id);
    LocalStorage.setItem(storageItems.recordingIds, JSON.stringify(recordingIds));
  }, [recordings]);

  const closeAddNoteModal = useCallback(() => {
    setIsAddNoteModalOpen(false);
    setCurrentRecording(null);
  }, []);

  const openAddNoteModal = useCallback(
    (recordingId: string) => {
      setCurrentRecording(recordings.find((recording) => recording.id === recordingId));
      setIsAddNoteModalOpen(true);
    },
    [recordings],
  );

  const updateCurrentRecording = (recording: RecordingType) => {
    setCurrentRecording(recording);
  };

  const selectRecording = useCallback((recordingId: string) => {
    setSelectedRecordingsIds((prev) => [...prev, recordingId]);
    setSelectedBulkOption(null);
  }, []);

  const deselectRecording = useCallback((recordingId: string) => {
    setSelectedRecordingsIds((prev) => prev.filter((id) => id !== recordingId));
    setSelectedBulkOption(null);
  }, []);

  const isRecordingSelected = useCallback(
    (recordingId: string) => {
      return !!selectedRecordingsIds.includes(recordingId);
    },
    [selectedRecordingsIds],
  );

  const selectAllRecordings = useCallback(() => {
    setSelectedRecordingsIds(recordings.map((recording) => recording.id));
  }, [recordings]);

  return (
    <RecordingsContext.Provider
      value={{
        updateCurrentRecording,
        openAddNoteModal,
        closeAddNoteModal,
        mutateRecordings,
        isAddNoteModalOpen,
        totalCount,
        pageSize,
        pageNumber: pageIndex,
        isLoading: isLoading || deleteBulk.isLoading || deleteIndividual.isLoading,
        recordings: recordings,
        error: error,
        currentRecording,
        totalPages,
        selectedRecordingsIds,
        selectRecording,
        deselectRecording,
        clearSelectedRecordingsIds,
        selectAllRecordings,
        isRecordingSelected,
        selectedBulkOption,
        setSelectedBulkOption,
        deleteIndividual: deleteIndividual.execute,
        deleteBulk: deleteBulk.execute as () => Promise<any>,
        tableState: controlledTableState,
      }}
    >
      {children}
    </RecordingsContext.Provider>
  );
};

export const useRecordingsContext = () => useContext(RecordingsContext);

export function getMappedPages(pages: PageType[]) {
  return pages
    .sort((a, b) => b.timestamp - a.timestamp)
    .map((page) => {
      const duration = formatSessionDuration(page.duration);
      const timestamp = moment(page.timestamp).format('L hh:mm A');

      return {
        url: page.url,
        topRightInfo: duration,
        bottomRightInfo: timestamp,
        privacyLevel: page.privacyLevel,
      };
    });
}
